import { forwardRef, InputHTMLAttributes, Ref } from 'react';
import styled, { css } from 'styled-components';
import { tokens } from 'styles/tokens';

export type TextInputProps = InputHTMLAttributes<HTMLInputElement> & {
    /**
     * Uses aria-invalid to mark the field as invalid and be styled as such.
     */
    invalid?: boolean;
    /**
     * The type of text input. Defaults to `text`.
     */
    type?: 'text' | 'search' | 'email' | 'password' | 'hidden' | 'number';
    /**
     * Whether or not the input should be automatically focused when it is rendered.
     */
    autofocus?: boolean;
    /**
     * Whether or not the text input can be edited.
     */
    readonly?: boolean;
    /**
     * Whether or not to show a green underline when the input is not invalid. Defaults to `false`.
     */
    showValidState?: boolean;

    pattern?: string;
    /**
     * The inputmode global attribute is an enumerated attribute that hints at the type of data that
     * might be entered by the user while editing the element or its contents.
     */
    inputMode?: 'text' | 'decimal' | 'numeric';

    /**
     * The padding to apply inside of the text input. Must be a valid CSS unit. Defaults to `1rem`.
     *
     * It is preferable to use this over setting padding through CSS because this handles layout
     * shift due to the border changing size.
     */
    padding?: string;

    /**
     * The width of the text input. Must be a valid CSS unit.
     */
    width?: string;
};

const dontPassProps = ['showValidState', 'padding', 'width'];
/**
 * The default padding to provide around the text inside the input.
 */
export const defaultInputPadding = '1rem';

const Input = styled.input.withConfig({
    shouldForwardProp: (p) => !dontPassProps.includes(p),
})<TextInputProps>`
    box-sizing: border-box;
    padding: ${({ padding }) => padding ?? defaultInputPadding} 0;
    ${({ width }) => (width ? `width: ${width};` : '')}
    border: 0;
    border-bottom: 1px solid ${tokens.input.border};
    background: transparent;
    color: ${tokens.text.primary};
    outline: none; /* remove default focus outline since we have our own focus state */

    /* [1]: prevent layout shift of larger border width when focused / invalid */
    padding-bottom: calc(${({ padding }) => padding ?? defaultInputPadding} + 1px);

    &:focus {
        border-color: ${tokens.border.focus};
        border-width: 2px;
        padding-bottom: ${({ padding }) =>
            padding ?? defaultInputPadding}; /* see comment [1] */
    }

    &:disabled {
        color: ${tokens.text.disabled};
    }

    &::placeholder {
        color: ${tokens.text.secondary};
    }

    &:disabled::placeholder {
        color: ${tokens.text.disabled};
    }

    &[aria-invalid='true'] {
        border-color: ${tokens.border.error};
        border-width: 2px;
        padding-bottom: ${({ padding }) =>
            padding ?? defaultInputPadding}; /* see comment [1] */
    }

    ${({ padding, showValidState }) =>
        showValidState &&
        css`
            &[aria-invalid='false'] {
                border-color: ${tokens.border.success};
                border-width: 2px;
                padding-bottom: ${() =>
                    padding ?? defaultInputPadding}; /* see comment [1] */
            }
        `}
`;

function TextInput(
    {
        type,
        invalid,
        readonly,
        autofocus,
        showValidState,
        autoComplete = 'off',
        ...rest
    }: TextInputProps,
    ref: Ref<HTMLInputElement>
) {
    return (
        <Input
            ref={ref}
            type={type ?? 'text'}
            aria-invalid={invalid}
            readOnly={readonly}
            autoFocus={autofocus}
            showValidState={showValidState ?? false}
            autoComplete={autoComplete}
            {...rest}
        />
    );
}

const forwardRefTextInput = forwardRef(TextInput);
export { forwardRefTextInput as TextInput };
